/**
 * Replenishment, browser side: which shortages are bought and which are made.
 *
 * One ordering count produces two outputs, not one:
 *
 *   ordering count → shortages → purchased items → vendor purchase orders
 *                              → prepared items  → prep list
 *
 * The rule that matters is the second arrow. A prepared item must never reach
 * a vendor purchase order, because there is no vendor to send it to: the only
 * way to put House Pizza Sauce on a PO is to invent a supplier called "House",
 * and a PO addressed to nobody is worse than no PO at all. The one exception
 * is an item that genuinely has a purchased alternative — a store that both
 * makes its own sauce and buys it when they run short — which is a real
 * purchase option with a real vendor, not a blank one.
 *
 * Like `countUnitModel.js`, this module does NO unit conversion. Prepared-item
 * quantities arrive already in the item's normalized unit (the count screen
 * normalized them; the batch yield was normalized when the recipe was linked),
 * so everything here is comparison and division. The canonical implementation
 * of the arithmetic lives at `supabase/functions/_shared/preparedItems.mjs`;
 * `preparedReplenishment.test.js` imports both and asserts they agree on the
 * same worked examples, so the two cannot drift apart unnoticed.
 */

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

const positive = (value) => {
  const n = toFiniteNumber(value);
  return n !== null && n > 0 ? n : null;
};

const round6 = (n) => Math.round(n * 1e6) / 1e6;

/** Prep-task statuses, in the order they happen. */
export const PREP_STATUSES = Object.freeze([
  'suggested', 'approved', 'released', 'in_progress', 'completed', 'canceled',
]);

/**
 * Statuses whose ingredient demand is real enough to buy against. A suggestion
 * the user has not accepted must not order tomatoes: the prep list would then
 * over-order every time somebody looked at it and walked away.
 */
export const DEMAND_CONTRIBUTING_STATUSES = Object.freeze([
  'approved', 'released', 'in_progress',
]);

/**
 * Is this row a prepared item? Reads the count line's snapshot as well as the
 * row itself, because an ordering screen sees both shapes.
 *
 * `is_subrecipe` is deliberately NOT consulted: it is the old display-only
 * flag, set on hand-typed catalog rows that have no recipe behind them, and
 * treating those as prepared would route them to a prep list that cannot say
 * what to make.
 */
export function isPreparedRow(row) {
  const type = row?.item_type ?? row?.item_snapshot?.item_type ?? null;
  return type === 'prepared';
}

/** True when a prepared item also has a legitimate way to buy it. */
export function hasPurchasedAlternative(row, optionsByItemId = {}) {
  if (row?.has_purchase_options === true) return true;
  const id = row?.inventory_item_id ?? row?.item_snapshot?.id ?? row?.id ?? null;
  if (id === null || id === undefined) return false;
  const options = optionsByItemId[String(id)] || optionsByItemId[id] || [];
  return options.some((o) => o?.status === 'active' && o?.is_approved !== false);
}

/**
 * Split one shortage calculation into vendor lines and prep lines.
 *
 * @param {Array} rows   need rows (count items or allocation needs)
 * @param {Object} [optionsByItemId]  inventory_item_id → purchase_options[]
 * @returns {{vendorRows: Array, prepRows: Array}}
 */
export function splitReplenishmentRows(rows, optionsByItemId = {}) {
  const vendorRows = [];
  const prepRows = [];
  (rows || []).forEach((row) => {
    if (!row) return;
    if (isPreparedRow(row) && !hasPurchasedAlternative(row, optionsByItemId)) {
      prepRows.push(row);
      return;
    }
    vendorRows.push(row);
  });
  return { vendorRows, prepRows };
}

/**
 * How many batches to prepare, and what that leaves on the shelf.
 *
 * Mirrors `planProduction` in the edge module — same rounding, same order of
 * operations (whole batches, then the increment, then the minimum). The
 * overage is returned rather than hidden: half a gallon of sauce nobody
 * planned for is the difference between a prep list the kitchen trusts and one
 * they stop reading.
 */
export function planPrepBatches({
  par, onHand, batchYieldNormalized, minimumBatches = 1, batchIncrement = 1,
} = {}) {
  const parQty = toFiniteNumber(par) ?? 0;
  const held = toFiniteNumber(onHand) ?? 0;
  const need = round6(parQty - held);

  if (!(need > 0)) {
    return { needed: false, need: Math.max(0, need), batches: null, expectedOutput: null, expectedAfter: held, overage: null, reason: null };
  }
  const perBatch = positive(batchYieldNormalized);
  if (perBatch === null) {
    return {
      needed: true, need, batches: null, expectedOutput: null, expectedAfter: null, overage: null,
      reason: 'The batch yield is not measurable, so a number of batches cannot be worked out.',
    };
  }
  const increment = positive(batchIncrement) ?? 1;
  const minimum = positive(minimumBatches) ?? 1;

  let batches = Math.ceil(round6(need / perBatch));
  batches = Math.ceil(round6(batches / increment)) * increment;
  if (batches < minimum) batches = Math.ceil(round6(minimum / increment)) * increment;

  const expectedOutput = round6(batches * perBatch);
  return {
    needed: true,
    need,
    batches: Math.round(batches * 1e4) / 1e4,
    expectedOutput,
    expectedAfter: round6(held + expectedOutput),
    overage: round6(expectedOutput - need),
    reason: null,
  };
}

/**
 * Turn prepared count rows into prep-list entries, ready to render or persist.
 * Rows an item has in several locations are one task: the sauce is made once,
 * not once per fridge.
 *
 * @param {Array} rows          prepared count rows for one session
 * @param {Object} configsByItemId  inventory_item_id → prepared_item_configs row
 */
export function buildPrepList(rows, configsByItemId = {}) {
  const groups = new Map();
  (rows || []).forEach((row) => {
    const id = row?.inventory_item_id ?? row?.item_snapshot?.id ?? row?.id ?? null;
    if (id === null || id === undefined) return;
    const key = String(id);
    if (!groups.has(key)) {
      groups.set(key, {
        inventory_item_id: id,
        description: row.description ?? row.item_snapshot?.description ?? null,
        par: 0,
        onHand: 0,
        counted: false,
      });
    }
    const group = groups.get(key);
    if (!group.description) group.description = row.description ?? row.item_snapshot?.description ?? null;
    // PAR is item-global; divergent legacy rows normalize to the max, exactly
    // as sharedOrderMath does for purchased items.
    const par = toFiniteNumber(row?.par_normalized_quantity)
      ?? toFiniteNumber(row?.par_level)
      ?? toFiniteNumber(row?.expected_quantity);
    if (par !== null && par > group.par) group.par = par;
    const isCounted = !!row?.is_counted || (row?.current_count !== '' && row?.current_count != null);
    if (isCounted) {
      group.onHand += toFiniteNumber(row?.current_count) ?? toFiniteNumber(row?.actual_quantity) ?? 0;
      group.counted = true;
    }
  });

  const tasks = [];
  groups.forEach((group) => {
    const config = configsByItemId[String(group.inventory_item_id)]
      || configsByItemId[group.inventory_item_id] || {};
    const plan = planPrepBatches({
      par: group.par,
      onHand: group.onHand,
      batchYieldNormalized: config.normalized_yield_quantity,
      minimumBatches: config.minimum_batches,
      batchIncrement: config.batch_increment,
    });
    if (!plan.needed) return;
    tasks.push({
      inventory_item_id: group.inventory_item_id,
      source_subrecipe_id: config.source_subrecipe_id ?? null,
      description: group.description,
      on_hand_normalized: group.onHand,
      par_normalized: group.par,
      need_normalized: plan.need,
      normalized_unit: config.normalized_unit ?? null,
      batch_yield_normalized: toFiniteNumber(config.normalized_yield_quantity),
      batches_planned: plan.batches,
      expected_output_normalized: plan.expectedOutput,
      expected_overage_normalized: plan.overage,
      expected_after_normalized: plan.expectedAfter,
      prep_area: config.prep_area ?? null,
      // A task we cannot turn into batches still belongs on the list, saying
      // why — dropping it would silently lose a real shortage.
      blocked_reason: plan.reason,
      status: 'suggested',
    });
  });
  return tasks;
}

/**
 * Group prep tasks the way a kitchen works — prep area, location, due date,
 * assignee, recipe category. Never by vendor: there isn't one, and a "vendor"
 * column full of blanks is how prepared items end up looking like a data
 * problem instead of a different kind of work.
 */
export const PREP_GROUPINGS = Object.freeze({
  prep_area: { key: 'prep_area', label: 'Prep area', fallback: 'Unassigned prep area' },
  location: { key: 'production_location', label: 'Location', fallback: 'Unassigned location' },
  due_date: { key: 'due_date', label: 'Due date', fallback: 'No due date' },
  assignee: { key: 'assigned_to_name', label: 'Assigned to', fallback: 'Unassigned' },
  category: { key: 'category', label: 'Category', fallback: 'Uncategorized' },
});

export function groupPrepTasks(tasks, groupBy = 'prep_area') {
  const spec = PREP_GROUPINGS[groupBy] || PREP_GROUPINGS.prep_area;
  const groups = new Map();
  (tasks || []).forEach((task) => {
    const raw = task?.[spec.key];
    const label = raw === null || raw === undefined || String(raw).trim() === ''
      ? spec.fallback
      : String(raw).trim();
    if (!groups.has(label)) groups.set(label, []);
    groups.get(label).push(task);
  });
  // The fallback group sorts last: real, named prep areas are what somebody
  // actually walks to.
  return [...groups.entries()]
    .map(([label, items]) => ({ label, items, isFallback: label === spec.fallback }))
    .sort((a, b) => {
      if (a.isFallback !== b.isFallback) return a.isFallback ? 1 : -1;
      return a.label.localeCompare(b.label);
    });
}

/**
 * Raw-ingredient demand from approved prep, ready to fold into vendor
 * ordering. Only approved-or-later tasks count.
 */
export function prepIngredientDemand(tasks, linesByRecipe = {}) {
  const demand = new Map();
  (tasks || []).forEach((task) => {
    if (!task || !DEMAND_CONTRIBUTING_STATUSES.includes(task.status)) return;
    const batches = positive(task.batches_planned);
    if (batches === null) return;
    const lines = linesByRecipe[String(task.source_subrecipe_id)] || [];
    lines.forEach((line) => {
      const itemId = line?.matched_inventory_item_id;
      const qty = positive(line?.qty);
      if (itemId === null || itemId === undefined || qty === null) return;
      const key = `${itemId}:${line.unit ?? ''}`;
      if (!demand.has(key)) {
        demand.set(key, {
          inventory_item_id: itemId,
          quantity: 0,
          unit: line.unit ?? null,
          from: [],
        });
      }
      const row = demand.get(key);
      row.quantity = round6(row.quantity + qty * batches);
      row.from.push({ prep_task_id: task.id ?? null, batches });
    });
  });
  return [...demand.values()];
}

/**
 * The replenishment review's two halves, in one shape the screen can render:
 * "Order from vendors" and "Prepare in house".
 */
export function buildReplenishmentReview({
  rows, optionsByItemId = {}, configsByItemId = {}, groupBy = 'prep_area',
} = {}) {
  const { vendorRows, prepRows } = splitReplenishmentRows(rows, optionsByItemId);
  const tasks = buildPrepList(prepRows, configsByItemId);
  return {
    vendorRows,
    prepTasks: tasks,
    prepGroups: groupPrepTasks(tasks, groupBy),
    counts: {
      vendorItems: new Set(vendorRows.map((r) => String(
        r.inventory_item_id ?? r.item_snapshot?.id ?? r.id,
      ))).size,
      prepItems: tasks.length,
      prepBatches: tasks.reduce((sum, t) => sum + (toFiniteNumber(t.batches_planned) ?? 0), 0),
      blockedPrepItems: tasks.filter((t) => t.blocked_reason).length,
    },
  };
}
